import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { AuthContext } from '../AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.insightos.co.uk';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeEdM8rAAAAANDYuqmGgLkqw9MmSv_2TzTEai6Q';

function LoginForm() {
  const { isAuthenticated, isVerifying } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({ email: '', password: '', companyCode: '' });
  const [error, setError] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [needsReset, setNeedsReset] = useState(false);

  useEffect(() => {
    // Wait until the initial auth check has finished before deciding to redirect.
    if (isVerifying) return;

    if (isAuthenticated) {
      navigate('/leads', { replace: true });
    }
  }, [isAuthenticated, isVerifying, navigate]);

  const handleChange = (event) => {
    setForm((prev) => ({ ...prev, [event.target.name]: event.target.value }));
    setError('');
    setNeedsReset(false);
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    setError('');
    if (!captchaToken) {
      setError('Please complete the CAPTCHA.');
      return;
    }

    if (!form.email.trim() || !form.password || !form.companyCode.trim()) {
      setError('Please complete email, password and company code.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE}/leads/login`,
        {
          email: form.email.trim(),
          password: form.password,
          companyCode: form.companyCode.trim(),
          captchaToken,
        },
        { withCredentials: true }
      );

      const data = response?.data;

      if (data?.success) {
        navigate('/leads/mfa', {
          replace: true,
          state: {
            email: form.email.trim(),
            companyCode: form.companyCode.trim(),
          },
        });
      } else {
        setError(data?.message || 'Login failed: incorrect email, password, or company code.');
        setNeedsReset(true);
      }
    } catch (error) {
      console.error('Login error:', error);
      const serverMessage = error?.response?.data?.message;
      setError(serverMessage || 'An error occurred while logging in. Please try again.');
      setNeedsReset(true);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setError('');

    if (!form.email.trim()) {
      setError('Please enter your email.');
      return;
    }

    if (!form.companyCode.trim()) {
      setError('Please enter your company code.');
      return;
    }

    if (!captchaToken) {
      setError('Please complete the CAPTCHA.');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_BASE}/api/forgot-password-sales`,
        {
          email: form.email.trim(),
          companyCode: form.companyCode.trim(),
          captchaToken,
        },
        { withCredentials: true }
      );

      if (response?.data?.success) {
        navigate('/leads/forgotpassword', {
          replace: true,
          state: {
            expiry: response?.data?.expiry,
            companyCode: form.companyCode.trim(),
          },
        });
        return;
      }

      setError('Failed to send reset link. Please check your email and company code.');
      setNeedsReset(true);
    } catch (forgotError) {
      console.error('Forgot password error:', forgotError);
      setError('An error occurred. Please try again.');
      setNeedsReset(true);
    } finally {
      setLoading(false);
    }
  };

  // Show a minimal spinner while the auth check is running to avoid a blank flash.
  if (isVerifying) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-purple-100/85 via-sky-100/80 to-emerald-100/85">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-indigo-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full bg-no-repeat bg-center bg-cover overflow-hidden bg-gradient-to-br from-purple-100/85 via-sky-100/80 to-emerald-100/85 px-4 py-6 sm:px-6"
     style={{ backgroundImage: "url('/nologo.png')" }}>
      <div className="pointer-events-none absolute -top-16 -left-20 h-72 w-72 rounded-full bg-purple-400/25 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 -right-20 h-72 w-72 rounded-full bg-sky-400/25 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 left-1/3 h-72 w-72 rounded-full bg-emerald-400/25 blur-3xl" />

      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-3rem)] w-full items-center justify-center">
      <div className="relative w-full max-w-md rounded-3xl border border-white/60 bg-white/60 p-6 shadow-[0_25px_80px_rgba(37,99,235,0.25)] backdrop-blur-xl sm:p-8 flex flex-col max-h-[95vh] overflow-y-auto">
        <div className="flex flex-col items-center text-center mb-4 sm:mb-6 flex-shrink-0">
          <img src="/avenor.png" alt={process.env.REACT_APP_IS_AVENOR !== 'true' ? 'InsightOS Avenor' : 'Avenor'} className="h-14 w-14 rounded-xl border border-white/70 bg-white/80 p-1.5 shadow-[0_10px_25px_rgba(76,29,149,0.2)] mb-2 sm:mb-3" />
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 mb-1">{showForgot ? 'Reset Password' : 'Sign in'}</h2>
          <p className="text-xs sm:text-sm text-slate-600 mb-2 sm:mb-3 md:mb-4">
            {showForgot ? 'Enter email and company code to start password reset.' : (process.env.REACT_APP_IS_AVENOR !== 'true' ? 'InsightOS Avenor secure access with MFA.' : 'Avenor secure access with MFA.')}
          </p>
          <button
            type="button"
            className="text-xs sm:text-sm font-semibold text-indigo-600 hover:text-indigo-500"
            onClick={() => {
              setShowForgot((prev) => !prev);
              setError('');
              setNeedsReset(false);
            }}
          >
            {showForgot ? 'Back to Sign In' : 'Forgot password?'}
          </button>
        </div>

        <form className="space-y-3 sm:space-y-4 pb-2 sm:pb-4" onSubmit={showForgot ? handleForgotPassword : handleLogin}>
          <div className="space-y-1">
            <label htmlFor="email" className="block text-xs sm:text-sm font-medium text-slate-800">Email Address</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={handleChange}
              className="w-full rounded-lg border border-white/70 bg-white/85 py-2 sm:py-2.5 px-3 text-sm sm:text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
            />
          </div>

          <div className="space-y-1">
            <label htmlFor="companyCode" className="block text-xs sm:text-sm font-medium text-slate-800">Customer ID</label>
            <input
              id="companyCode"
              name="companyCode"
              type="text"
              required
              value={form.companyCode}
              onChange={handleChange}
              className="w-full rounded-lg border border-white/70 bg-white/85 py-2 sm:py-2.5 px-3 text-sm sm:text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
            />
          </div>

          {!showForgot && (
            <div className="space-y-1">
              <label htmlFor="password" className="block text-xs sm:text-sm font-medium text-slate-800">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={form.password}
                onChange={handleChange}
                className="w-full rounded-lg border border-white/70 bg-white/85 py-2 sm:py-2.5 px-3 text-sm sm:text-base text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
              />
            </div>
          )}

          <div className="flex justify-center py-2">
            <div className="transform scale-90 sm:scale-100 origin-center">
              <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} onChange={(value) => setCaptchaToken(value || '')} />
            </div>
          </div>

          {error && <div className="text-red-500 text-center text-xs sm:text-sm px-2">{error}</div>}

          <div className="pt-1 sm:pt-2 pb-1">
            {needsReset ? (
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="w-full rounded-lg bg-red-600 text-sm sm:text-base font-semibold py-2.5 sm:py-3 text-white shadow-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Reset
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className={`w-full rounded-lg bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 active:from-indigo-700 active:to-violet-700 text-white text-sm sm:text-base font-semibold py-2.5 sm:py-3 transition-all shadow-lg shadow-indigo-500/30 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${loading ? 'opacity-60 cursor-not-allowed' : ''}`}
              >
                {showForgot ? (loading ? 'Sending reset...' : 'Send Reset Link') : (loading ? 'Sending code...' : 'Sign In')}
              </button>
            )}
          </div>
        </form>
      </div>
      </div>
    </div>
  );
}

export default LoginForm;
